import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import http from "@/api";
import { useAuth } from "@/auth";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

const STORAGE_KEYS = {
  admin: "lala_tour_seen_admin",
  evaluator: "lala_tour_seen_evaluator",
  buyer: "lala_tour_seen_buyer",
  stakeholder: "lala_tour_seen_stakeholder",
  supplier: "lala_tour_seen_supplier",
};

const ADMIN_STEPS = [
  {
    path: "/dashboard", selector: '[data-testid="completeness-heatmap"]',
    title: "Completitud del mes",
    body: "Este heatmap te dice quién va atrasado: filas = pilares, columnas = evaluadores, cada celda con su % capturado. Cambia el periodo con el selector de mes.",
  },
  {
    path: "/dashboard", selector: '[data-testid="weekly-digest"]',
    title: "Resumen semanal",
    body: "Tu actividad de los últimos 7 días: capturas registradas, ajustes de pesos por categoría, proveedores que cambiaron de nivel y solicitudes de cambio pendientes.",
  },
  {
    path: "/dashboard", selector: '[data-testid="trends-panel"]',
    title: "Desempeño histórico",
    body: "La evolución del score por periodo, global o filtrada por categoría de compra. Solo lectura — ideal para juntas de liderazgo.",
  },
  {
    path: "/comprador/mis-proveedores", selector: '[data-testid="download-template-btn"]',
    title: "Aquí subes proveedores",
    body: "Descarga la plantilla CSV, llénala (nombre, RFC, SAP ID, categoría, comprador) y súbela. La vista previa detecta RFC y SAP ID duplicados antes de confirmar.",
  },
  {
    path: "/admin/usuarios", selector: '[data-testid="internal-users-list"]',
    title: "Evaluadores, pilares y permisos",
    body: "Presiona \u201CEditar\u201D en un evaluador → pestaña Alcance → \u201CPilares asignados\u201D. Con eso solo podrá capturar SU pilar. A los category managers les asignas su categoría de compra aquí mismo.",
  },
  {
    path: "/admin/catalogo-kpis", selector: '[data-testid^="edit-kpi-"]',
    title: "KPIs y frecuencias",
    body: "Edita cualquier indicador (ícono de lápiz) para definir su periodicidad — mensual, bimestral, trimestral, semestral o anual — sus niveles 1–4 y su categoría de compra.",
  },
  {
    path: "/admin/configuracion", selector: '[data-testid="config-flow"]',
    title: "Flujo de configuración del programa",
    body: "Los pasos del programa en orden: Pesos, catálogo, reconocimiento, usuarios… Cada paso marca ✓ configurado o ◌ pendiente.",
  },
  {
    path: "/admin/configuracion", selector: '[data-testid="pending-weights-banner"],[data-testid="pending-weights-ok"]',
    title: "Pesos por categoría de compra",
    body: "Cada categoría pondera sus pilares de forma distinta. Este aviso te muestra qué categorías del piloto aún usan los pesos globales — su category manager (o tú) puede definir los propios.",
  },
  {
    path: "/admin/configuracion", selector: '[data-testid="weights-form"]',
    action: { selector: '[data-testid="weights-category-select"]', value: "Empaques" },
    title: "Ejemplo: Empaques",
    body: "Empaques pondera Calidad 30, Costo 20, Continuidad 15, Sostenibilidad 10, Innovación 15 y Servicio 10. La suma siempre debe dar 100% — la plataforma lo valida antes de guardar.",
  },
  {
    path: "/admin/configuracion", selector: '[data-testid="weights-simulate-btn"]',
    action: { selector: '[data-testid="weights-category-select"]', value: "Empaques" },
    title: "Simula antes de guardar",
    body: "Con «Simular impacto» ves qué proveedores cambiarían de nivel ANTES de aplicar. Al guardar se muestra la reclasificación exacta y todo queda en el historial, exportable a Excel para auditorías.",
  },
  {
    path: "/admin/programa", selector: '[data-testid="pilot-scope-section"]',
    title: "Alcance del piloto",
    body: "Las categorías activas del piloto (Empaques, Ingredientes, Materia prima cárnica, Asépticos, Maquilas Co-Man). Los proveedores de otras categorías quedan visibles pero \u201CFuera del piloto\u201D.",
  },
  {
    path: "/captura-mensual", selector: '[data-testid="cm-view-toggle"]',
    title: "Captura mensual",
    body: "Como admin puedes capturar cualquier pilar, por proveedor o por pilar. Los evaluadores solo ven el suyo. Escala 1–4 con autosave; la presentación al proveedor es en base 10.",
  },
  {
    path: "/admin/configuracion", selector: '[data-testid="report-policy-card"]',
    title: "Reporte mensual del proveedor",
    body: "Aquí decides cómo obtiene el proveedor su reporte ejecutivo PDF: descarga directa con un clic (modo actual), con autorización previa, de pago o deshabilitado. El cambio aplica al instante.",
  },
  {
    path: "/proveedores", selector: '[data-testid="directory-legend"]',
    title: "Semáforo de riesgo y medallas",
    body: "Cada tarjeta muestra la medalla oficial de su nivel y un punto de color: verde = KPIs en meta, amarillo = requiere seguimiento, rojo = riesgo alto. La insignia ECO indica sustentabilidad destacada.",
  },
  {
    path: "/admin/aprobaciones", selector: '[data-testid="cr-list"],[data-testid="cr-empty"]',
    title: "Aprobaciones y solicitudes de cambio",
    body: "Aquí publicas evaluaciones y resuelves las Solicitudes de cambio del liderazgo: apruebas o rechazas con motivo. Todo queda en bitácora y el solicitante recibe notificación.",
  },
  {
    path: "/admin/aprendizaje", selector: '[data-testid="resources-table"]',
    title: "Centro de Aprendizaje",
    body: "Sube videos, PDFs, artículos y cursos con quiz para tus proveedores y equipo. También administras desde aquí el video de bienvenida del inicio. Puedes fijar contenido, ordenar y ver el progreso de quien lo consume.",
  },
  {
    path: "/dashboard", selector: null,
    title: "¡Listo!",
    body: "Ese es el ciclo completo: proveedores → pilares → captura → seguimiento → resultados → gobernanza. Repite este tour cuando quieras con el botón ✦ del encabezado, o consulta la Guía de uso.",
  },
];

const EVALUATOR_STEPS = [
  {
    path: "/dashboard", selector: '[data-testid="trends-panel"]',
    title: "Tu vista general",
    body: "Aquí ves el desempeño global del programa: tendencias por categoría, reconocimientos y alertas de desarrollo. Todo en modo lectura salvo tu pilar.",
  },
  {
    path: "/captura-mensual", selector: '[data-testid="cm-scope-banner"]',
    title: "Tu pilar asignado",
    body: "Solo puedes alimentar el/los pilares que Lala te asignó. Los demás pilares los verás en el dashboard y en el detalle de cada proveedor, pero en modo lectura.",
  },
  {
    path: "/captura-mensual", selector: '[data-testid="cm-pv-period-input"]',
    title: "Elige el periodo",
    body: "Selecciona el mes a capturar. Los indicadores trimestrales, semestrales o anuales solo aparecen en su mes correspondiente — si no ves alguno, es por su periodicidad.",
  },
  {
    path: "/captura-mensual", selector: '[data-testid^="cm-pv-supplier-"]',
    title: "Captura por proveedor (nivel 1–4)",
    body: "Aquí están TODOS los proveedores con su barra de avance. Expande uno, asigna nivel 1–4 en Objetivo y Real por indicador y agrega notas. Todo se guarda solo (autosave).",
  },
  {
    path: "/proveedores", selector: '[data-testid="suppliers-search"]',
    title: "Consulta cualquier proveedor",
    body: "Busca por nombre o SAP ID y abre su detalle: reconocimiento en base 10, pesos de su categoría, histórico y camino de mejora.",
  },
  {
    path: "/captura-mensual", selector: null,
    title: "¿Detectaste un error de otro pilar?",
    body: "No lo puedes editar tú — abre el detalle del proveedor y usa el botón «Solicitar cambio» con tu justificación. El administrador lo resolverá y te llegará notificación. Repite este tour con el botón ✦.",
  },
];

const BUYER_STEPS = [
  {
    path: "/comprador", selector: '[data-testid="buyer-dashboard"]',
    title: "Tu tablero de comprador",
    body: "El desempeño de los proveedores de tu categoría: ranking, niveles de reconocimiento y filtros por periodo. Solo ves los proveedores que tienes asignados.",
  },
  {
    path: "/comprador/pesos", selector: '[data-testid="weights-form"]',
    title: "Pesos de tu categoría",
    body: "Como category manager TÚ defines cuánto pesa cada pilar en tu categoría. Deben sumar 100%. Si no defines pesos propios, se usan los globales del programa.",
  },
  {
    path: "/comprador/pesos", selector: '[data-testid="weights-save-btn"]',
    title: "Simula antes de guardar",
    body: "Con «Simular impacto» ves qué proveedores cambiarían de nivel ANTES de aplicar los pesos. Al guardar, la plataforma te muestra la reclasificación exacta y queda en el historial de auditoría.",
  },
  {
    path: "/captura-mensual", selector: '[data-testid="cm-view-toggle"]',
    title: "Captura mensual",
    body: "Captura el nivel 1–4 (Objetivo y Real) de los indicadores de tus proveedores. Autosave activado; los indicadores no mensuales solo aparecen en su mes.",
  },
  {
    path: "/aprendizaje", selector: '[data-testid="learning-page"]',
    title: "Centro de Aprendizaje",
    body: "Cápsulas, guías y cursos del programa. Útil para responder dudas de tus proveedores sobre cómo funciona su evaluación.",
  },
  {
    path: "/comprador", selector: null,
    title: "¡Listo!",
    body: "Tu ciclo: revisa tu tablero → define/ajusta pesos de tu categoría → captura el mes → acompaña a tus proveedores. Repite este tour cuando quieras con el botón ✦ del encabezado.",
  },
];

const STAKEHOLDER_STEPS = [
  {
    path: "/dashboard", selector: '[data-testid="trends-panel"]',
    title: "Vista general ejecutiva",
    body: "Tendencias por categoría, distribución por nivel de reconocimiento y alertas de desarrollo. Tu acceso es de solo lectura: nadie edita resultados directamente.",
  },
  {
    path: "/proveedores", selector: '[data-testid="suppliers-search"]',
    title: "Directorio de proveedores",
    body: "Busca por nombre o SAP ID, filtra por categoría o nivel. Cada tarjeta muestra la medalla oficial del nivel y el semáforo de riesgo (la guía de colores está arriba de la lista). Abre cualquier proveedor para ver su reconocimiento en base 10, pesos de su categoría e histórico.",
  },
  {
    path: "/admin/catalogo-kpis", selector: '[data-testid="equivalence-table"]',
    title: "Catálogo oficial y tabla de equivalencias",
    body: "El catálogo oficial de indicadores en 6 pilares con sus rúbricas explícitas de 4 niveles. Esta tabla muestra cómo la captura interna 1–4 se convierte a tu calificación base 10: 1 = 2.5, 2 = 5.0, 3 = 7.5 y 4 = 10.",
  },
  {
    path: "/dashboard", selector: null,
    title: "¿Detectaste una inconsistencia?",
    body: "En el detalle de cualquier proveedor usa «Solicitar cambio» con tu justificación (mínimo 20 caracteres). El administrador lo resuelve y recibes notificación — todo queda auditado. Repite este tour con el botón ✦.",
  },
];

const SUPPLIER_STEPS = [
  {
    path: "/mi-evaluacion", selector: '[data-testid="my-score"]',
    title: "Tu calificación",
    body: "Tu score con Lala. Las calificaciones se presentan en base 10; se construyen con la captura mensual del equipo Lala en los 6 pilares.",
  },
  {
    path: "/mi-evaluacion", selector: '[data-testid="recognition-panel"]',
    title: "Tu reconocimiento LALA Elite",
    body: "Tu nivel (Platino, Oro, Plata, Bronce), la calificación por pilar y cuánto pesa cada pilar en TU categoría de compra. Incluye tu histórico de 3, 6 o 12 meses.",
  },
  {
    path: "/mi-evaluacion", selector: '[data-testid="report-block"]',
    title: "Tu reporte ejecutivo",
    body: "Descárgalo con un solo clic: PDF con el detalle de tu evaluación, benchmark de tu categoría, tu medalla oficial y recomendaciones para subir de nivel.",
  },
  {
    path: "/mi-evaluacion", selector: '[data-testid="digital-seal"]',
    title: "Tu distintivo y certificado",
    body: "Descarga tu sello digital (PNG o SVG) con la medalla de tu nivel, tu certificado oficial y comparte el enlace público verificable en tu sitio o redes.",
  },
  {
    path: "/aprendizaje", selector: '[data-testid="learning-page"]',
    title: "Centro de Aprendizaje",
    body: "Cápsulas, guías y cursos para crecer con Lala: qué mide cada pilar, cómo subir de nivel y las mejores prácticas de la comunidad Elite.",
  },
  {
    path: "/mi-evaluacion", selector: null,
    title: "Crezcamos juntos",
    body: "Tu evaluación se actualiza cada mes. Si algo no cuadra, contacta a tu comprador Lala. Repite este tour cuando quieras con el botón ✦ del encabezado.",
  },
];

const TOURS = {
  admin: ADMIN_STEPS,
  evaluator: EVALUATOR_STEPS,
  buyer: BUYER_STEPS,
  stakeholder: STAKEHOLDER_STEPS,
  supplier: SUPPLIER_STEPS,
};


export default function AdminTour() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(null);
  const [rect, setRect] = useState(null);
  const [version, setVersion] = useState(null);
  const STEPS = TOURS[user?.role] || [];
  const STORAGE_KEY = STORAGE_KEYS[user?.role];

  // Auto-inicio si el usuario no ha visto la versión vigente del tour (reactivable por admin).
  // Solo se auto-lanza si el usuario ya está en la página inicial del tour: nunca lo saca de su ruta actual.
  useEffect(() => {
    if (!TOURS[user?.role]) { setStep(null); return; }
    let alive = true;
    http.get("/tour/version").then(({ data }) => {
      if (!alive) return;
      setVersion(data.version);
      const seen = Number(localStorage.getItem(STORAGE_KEYS[user.role]) || 0);
      const firstPath = TOURS[user.role][0]?.path;
      if (seen < data.version && window.location.pathname === firstPath) {
        setTimeout(() => setStep(0), 1500);
      }
    }).catch(() => setVersion(1));
    return () => { alive = false; };
  }, [user]);

  // Relanzable desde el botón del header
  useEffect(() => {
    const h = () => { setStep(0); };
    window.addEventListener("lala:start-tour", h);
    return () => window.removeEventListener("lala:start-tour", h);
  }, []);

  // Al cambiar de paso: navegar a la página, ejecutar acción opcional y localizar el elemento
  useEffect(() => {
    if (step == null) return;
    const s = STEPS[step];
    if (!s) { setStep(null); return; }
    if (window.location.pathname !== s.path) nav(s.path);
    setRect(null);
    let tries = 0;
    let actionDone = !s.action;
    const t = setInterval(() => {
      tries += 1;
      if (tries > 24) { clearInterval(t); return; }
      if (!actionDone) {
        const ael = document.querySelector(s.action.selector);
        if (ael) {
          try {
            const setter = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, "value").set;
            setter.call(ael, s.action.value);
            ael.dispatchEvent(new Event("change", { bubbles: true }));
          } catch (e) { /* noop */ }
          actionDone = true;
        }
        return;
      }
      if (!s.selector) { clearInterval(t); return; }
      const el = document.querySelector(s.selector);
      if (el) {
        clearInterval(t);
        el.scrollIntoView({ block: "center" });
        setTimeout(() => {
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        }, 350);
      }
    }, 250);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  if (!user || !TOURS[user.role] || step == null || !STEPS[step]) return null;

  const s = STEPS[step];
  const last = step === STEPS.length - 1;
  const finish = () => { localStorage.setItem(STORAGE_KEY, String(version || 1)); setStep(null); };

  const CARD_H = 250;
  let cardStyle;
  if (rect) {
    const below = rect.top + rect.height + CARD_H + 14 < window.innerHeight;
    let top = below ? rect.top + rect.height + 14 : rect.top - CARD_H - 14;
    top = Math.min(Math.max(top, 16), window.innerHeight - CARD_H - 16);
    cardStyle = {
      position: "fixed",
      left: Math.min(Math.max(rect.left, 16), window.innerWidth - 416),
      top,
    };
  } else {
    cardStyle = { position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)" };
  }

  return (
    <div className="fixed inset-0 z-[100]" data-testid="admin-tour">
      {rect ? (
        <div
          className="fixed rounded-xl pointer-events-none transition-all duration-300"
          style={{
            top: rect.top - 6, left: rect.left - 6,
            width: rect.width + 12, height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(10, 20, 40, 0.62)",
            border: "2px solid var(--lala-yellow, #FFC400)",
          }}
          data-testid="tour-spotlight"
        />
      ) : (
        <div className="fixed inset-0 bg-[rgba(10,20,40,0.62)]" />
      )}

      <div className="w-[400px] max-w-[calc(100vw-32px)] bg-[var(--cream)] sketch-border p-5 shadow-2xl" style={cardStyle} data-testid="tour-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-hand text-lg text-[var(--lala-blue)] flex items-center gap-1.5">
              <Sparkles size={14} /> tour guiado · paso {step + 1} de {STEPS.length}
            </div>
            <div className="font-display text-xl mt-0.5" data-testid="tour-title">{s.title}</div>
          </div>
          <button onClick={finish} className="p-1 rounded hover:bg-[var(--cream-soft)]" title="Salir del tour" data-testid="tour-close">
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed" data-testid="tour-body">{s.body}</p>
        <div className="flex items-center justify-between mt-4">
          <button onClick={finish} className="text-xs text-[var(--ink-soft)] hover:underline" data-testid="tour-skip">
            Saltar tour
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)}
                className="sketch-pill bg-white border border-[var(--lala-blue)] text-[var(--lala-blue)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
                data-testid="tour-prev">
                <ArrowLeft size={12} /> Anterior
              </button>
            )}
            <button onClick={() => (last ? finish() : setStep(step + 1))}
              className="sketch-pill bg-[var(--lala-blue)] text-white px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
              data-testid="tour-next">
              {last ? "Terminar" : "Siguiente"} {!last && <ArrowRight size={12} />}
            </button>
          </div>
        </div>
        <div className="flex gap-1 mt-3" data-testid="tour-progress">
          {STEPS.map((_, i) => (
            <span key={i} className="h-1 flex-1 rounded-full"
              style={{ background: i <= step ? "var(--lala-blue)" : "var(--line, #E1E4EE)" }} />
          ))}
        </div>
      </div>
    </div>
  );
}
